import { useState } from 'react';
import { Check, Plus } from 'lucide-react';

import ActionButton from '../../../components/Buttons/ActionButton';
import InputField from '../../../components/Forms/Input/InputField';
import SelectField from '../../../components/Forms/Input/SelectField';
import { RestaurantPreparationStationDTO } from '../../../types/restaurant/RestaurantPreparationStationTypes';

interface Props {
  stations: RestaurantPreparationStationDTO[];
  value: number | '';
  disabled?: boolean;
  onChange: (stationId: number | '') => void;
  onCreateStation: (
    name: string
  ) => Promise<RestaurantPreparationStationDTO | null>;
}

/**
 * Selector de estación de preparación con alta "＋ Nueva estación" en línea
 * (mini-formulario: solo el nombre; el código lo genera el backend).
 */
const StationQuickField = ({
  stations,
  value,
  disabled,
  onChange,
  onCreateStation,
}: Props) => {
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const addStation = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const created = await onCreateStation(name);
      if (created) {
        onChange(created.id);
        setShowNew(false);
        setNewName('');
      }
    } finally {
      setCreating(false);
    }
  };

  if (showNew) {
    return (
      <div className="space-y-2 rounded-lg border border-primary/20 bg-primary/5 p-3 dark:border-primary/25 dark:bg-primary/10">
        <InputField
          label="Nueva estación de preparación"
          name="stationNew"
          type="text"
          iconKey="store"
          value={newName}
          placeholder="Ej. Parrilla"
          disabled={disabled || creating}
          onChange={(event) => setNewName(event.target.value)}
        />
        <div className="flex items-center gap-2">
          <ActionButton
            label="Agregar"
            icon={<Check className="h-4 w-4" />}
            variant="primary"
            size="sm"
            fullWidth={false}
            loading={creating}
            disabled={!newName.trim() || creating || disabled}
            onClick={addStation}
          />
          <button
            type="button"
            className="text-xs text-bodydark2 hover:underline"
            onClick={() => setShowNew(false)}
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <SelectField
        label="Estación de preparación"
        name="preparationStationId"
        value={value}
        iconKey="store"
        disabled={disabled}
        placeholder="Sin estación asignada"
        options={[
          { value: '', label: 'Sin estación asignada' },
          ...stations.map((station) => ({
            value: station.id,
            label: station.name,
          })),
        ]}
        onChange={(event) =>
          onChange(event.target.value === '' ? '' : Number(event.target.value))
        }
      />
      <button
        type="button"
        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        onClick={() => setShowNew(true)}
      >
        <Plus className="h-3.5 w-3.5" />
        Nueva estación
      </button>
    </div>
  );
};

export default StationQuickField;
